import { isNil, LinearUnit, UnitTypeId, UomConverter, uomLinearUnits } from '@trimble-gcs/common';
import { Scan3dStyleUom } from '../models/scan-3d-style-uom';

export class Scan3dUomLinearConverter {
  private uomConverter = new UomConverter(uomLinearUnits);

  constructor(private readonly defaultUnit = LinearUnit.Meter) {}

  toUom(value: number, uom?: Scan3dStyleUom): number {
    if (isNil(uom)) return value;

    return (
      this.uomConverter.convert(
        value,
        this.defaultUnit,
        uom.lengthUnit,
        UnitTypeId.Linear,
        uom.lengthDecimals,
      ).roundedValue ?? value
    );
  }

  toDefaultUom(value: number, uom?: Scan3dStyleUom): number {
    if (isNil(uom)) return value;

    return (
      this.uomConverter.convert(value, uom.lengthUnit, this.defaultUnit, UnitTypeId.Linear).value ??
      value
    );
  }

  toFeetInchesFraction(
    value: number,
    uom: Scan3dStyleUom,
  ): {
    feet: number;
    inches: number;
    fraction: number;
  } {
    const negative = value < 0;
    const inchUom: Scan3dStyleUom = {
      lengthUnit: LinearUnit.Inch,
      lengthDecimals: 10,
      lengthFractions: 0,
    };

    // Calculate feet
    const totalInches = this.toUom(Math.abs(value ?? 0), inchUom);
    const feet = Math.floor(totalInches / 12);

    // Calculate inches
    const remainingInches = totalInches - feet * 12;
    const inches = Math.floor(remainingInches);

    // Calculate fraction
    const fractionalInches = remainingInches - inches;
    const fraction = Math.floor(fractionalInches * uom.lengthFractions);

    return {
      feet: negative ? feet * -1 : feet,
      inches,
      fraction,
    };
  }

  fromFeetInchesFraction(
    feet: number,
    inches: number,
    fraction: number,
    uom: Scan3dStyleUom,
  ): number {
    const negative = feet < 0;
    const feetAbs = Math.abs(feet);

    const totalInches = feetAbs * 12 + inches + fraction / uom.lengthFractions;
    const signedInches = negative ? -totalInches : totalInches;

    const inchUom: Scan3dStyleUom = { ...uom, lengthUnit: LinearUnit.Inch };
    return this.toDefaultUom(signedInches, inchUom);
  }
}
